import React from "react";
import Avatar from "../common/Avatar";
import AnonSilhouetteAvatar from "./AnonSilhouetteAvatar";
import FlagImg from "../agent-tender/FlagImg";
import { UrgencyPill, BuyLoanChips, ReadyToFlyPill, ShortTermPill, SalaryBandPill, NotReadyForLine } from "./LmPills";
import { ShareGlyph, LockGlyph, InfoIGlyph, MagnifierGlyph, AgencyBadge } from "./lmGlyphs";
import {
  formatMarketValueExact,
  formatMarketValueRange,
  formatSalaryBand,
  daysLeftShort,
} from "../../utils/lastMinute";
import { formatDistanceToNowStrict } from "date-fns";

// The shared Last Minute browse card — V2 UI rebuild 1:1 from the design
// handoff (cards 1a/1c, reveal 2a, quick-view loupe 9f, mobile 8a). ONE
// component renders both variants:
//   • anonymized (is_named === false): silhouette, headline "{position}, {age}",
//     MV range, padlock "Name revealed on interest" — never a name/current club.
//   • named (is_named === true): photo/initials, real name + club + exact MV.
// The 3-slot footer ([primary · Add to radar · share]) is identical for every
// viewer (R2 "identical action placement"); the CALLER supplies the primary node
// so the same card serves club "Contact agent", agent "I have an opportunity",
// "Request pending", and "Open chat" without the card knowing the surface.
const timeAgo = (iso) => {
  try {
    return `${formatDistanceToNowStrict(new Date(iso))} ago`;
  } catch (e) {
    return "";
  }
};

// Artboard compact "2h ago" style ("8h ago · 14d left"): shorten date-fns words.
const timeAgoShort = (iso) => timeAgo(iso)
  .replace(/ minutes?/, "m").replace(/ hours?/, "h").replace(/ days?/, "d")
  .replace(/ months?/, "mo").replace(/ years?/, "y").replace(/ seconds?/, "s");

const ListingCard = ({
  listing,
  primary = null,
  onAddToRadar = null,
  radarActive = false,
  onShare = null,
  onQuickView = null,
  viewerClubName = "",
  viewerCountry = "",
}) => {
  const named = !!listing.is_named;
  // A listing that is anonymized BUT named to this viewer = revealed to my club
  // only (Invariant 3). Distinct from a natively-named listing → show the banner.
  const revealed = !!listing.anonymized && named;
  const forSale = listing.urgency_class === "for_sale";
  const mv = named ? formatMarketValueExact(listing.market_value) : formatMarketValueRange(listing.market_value);
  const salary = formatSalaryBand(listing);
  // Artboard third line: "MV €4.5M · Portugal". The feed serializer exposes the
  // current CLUB (player_club) but no country field, so the club renders in that
  // slot (see TODO — country needs a BE serializer field).
  const secondLine = [mv, named ? listing.player_club : null].filter(Boolean).join(" · ");
  const headline = named
    ? listing.player_name || "Player"
    : [listing.position, listing.age].filter((v) => v !== null && v !== undefined && v !== "").join(", ");
  const agent = listing.agent || {};
  const blocked = !!listing.blocked;
  // Nationality flags after the name (artboard 1a). The feed serializer does not
  // expose nationalities yet — renders only if/when the field appears (TODO).
  const nationalities = Array.isArray(listing.nationalities) ? listing.nationalities : [];

  return (
    <div
      className="bg-white rounded-2xl p-4 sm:p-[18px] border flex flex-col gap-[11px] sm:gap-3"
      style={
        revealed
          ? { borderColor: "#BDD9FF", boxShadow: "0 0 0 3px rgba(34,131,255,.1), 0 1px 2px rgba(16,24,40,.05)" }
          : { borderColor: "#E4E9F1", boxShadow: "0 1px 2px rgba(16,24,40,.05)" }
      }
      data-testid="lm-listing-card"
    >
      {/* Reveal banner (design 2a "after") — only this club sees the name */}
      {revealed && (
        <div className="flex items-center gap-2 rounded-[9px] bg-[#F0F7FF] border border-[#CFE3FB] px-2.5 py-[7px] text-[12px] font-semibold text-[#1466D8] leading-[1.4]">
          <span className="w-[6px] h-[6px] rounded-full bg-[#2283FF] flex-none" />
          Profile revealed to {viewerClubName || "your club"}. Only your club sees the name
        </div>
      )}

      {/* Header: subject + urgency pill */}
      <div className="flex items-start gap-3">
        {named ? (
          <Avatar src={null} alt={headline} fallbackText={headline} size={52} className="flex-none" />
        ) : (
          <AnonSilhouetteAvatar size={52} />
        )}
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-[7px] min-w-0">
            <h3 className="text-[16px] font-bold text-[#14263D] truncate">{headline}</h3>
            {named &&
              nationalities.map((n) => (
                <FlagImg key={n.code || n.name || n} country={n.code || n.name || n} className="w-[17px] h-3 rounded-[2.5px]" />
              ))}
          </div>
          {/* Named cards repeat the position line under the name; anonymized cards
              show the "Anonymized profile" sub-label instead (position/age already
              live in the headline) — design 1a. */}
          <p className="mt-[3px] text-[12.5px] font-medium text-[#5A6B82] truncate">
            {named
              ? [listing.position, listing.age != null ? `${listing.age} yrs` : null].filter(Boolean).join(" · ")
              : "Anonymized profile"}
          </p>
          {secondLine && <p className="mt-[2px] text-[12px] text-[#8A98AC] truncate">{secondLine}</p>}
        </div>
        <span className="flex-shrink-0">
          <UrgencyPill urgency={listing.urgency_class} />
        </span>
      </div>

      {/* Flags row: BUY/LOAN + salary band + attribute pills */}
      {(forSale || salary || listing.ready_to_fly || listing.short_term) && (
        <div className="flex flex-wrap items-center gap-1.5">
          {forSale && <BuyLoanChips buy={listing.for_sale_buy} loan={listing.for_sale_loan} />}
          {salary && <SalaryBandPill label={salary} />}
          {listing.ready_to_fly && <ReadyToFlyPill />}
          {listing.short_term && <ShortTermPill />}
        </div>
      )}

      {/* Not-ready-for (public blacklist line, image flags) */}
      {listing.not_ready_for?.length > 0 && <NotReadyForLine countries={listing.not_ready_for} />}

      {/* One line of context + the quick-view loupe (design 9f: truncated quote,
          magnifier opens the full text for every card) */}
      {(listing.note || onQuickView) && (
        <div className="flex items-center gap-2">
          {listing.note ? (
            <span className="flex-1 text-[12.5px] text-[#5A6B82] leading-[1.5] overflow-hidden text-ellipsis whitespace-nowrap">
              “{listing.note}”
            </span>
          ) : (
            <span className="flex-1" />
          )}
          {onQuickView && (
            <button
              type="button"
              onClick={() => onQuickView(listing)}
              className="flex-none w-[30px] h-[30px] rounded-full bg-white inline-flex items-center justify-center shadow-[0_1px_3px_rgba(34,131,255,.2)]"
              style={{ border: "1.5px solid #BDD9FF" }}
              aria-label="Quick view"
              title="Quick view"
              data-testid="lm-quickview-loupe"
            >
              <MagnifierGlyph size={13} color="#1466D8" />
            </button>
          )}
        </div>
      )}

      {/* Anonymized lock line — CSS padlock verbatim, never an emoji */}
      {!named && (
        <p className="flex items-center gap-[7px] text-[12px] text-[#8A98AC]">
          <LockGlyph size="card" />
          Name revealed on interest
        </p>
      )}

      {/* Divider + agent row */}
      <div className="h-px bg-[#EEF1F6] mt-auto" />
      <div className="flex items-center gap-2">
        <Avatar src={null} fallbackText={agent.name} size={24} />
        <span className="text-[12.5px] font-semibold text-[#33475E] truncate">{agent.name}</span>
        <AgencyBadge agency={agent.agency} />
        {agent.agency && <span className="text-[12px] text-[#8A98AC] truncate">{agent.agency}</span>}
        <span className="ml-auto text-[11.5px] text-[#98A5B8] whitespace-nowrap flex-shrink-0">
          {[listing.created_at ? timeAgoShort(listing.created_at) : "", daysLeftShort(listing.expires_at)]
            .filter(Boolean)
            .join(" · ")}
        </span>
      </div>

      {/* Blocked-contact calm note (Invariant 5) — same data as "Not ready for" */}
      {blocked && (
        <div className="flex items-start gap-[7px] rounded-[9px] bg-[#F4F6FA] px-2.5 py-2 text-[12px] text-[#7A8AA0] leading-[1.45]">
          <InfoIGlyph size={14} />
          <span>
            {listing.listing_type === "staff" ? "This person" : "This player"} is not open to moves to{" "}
            {viewerCountry || "your country"} right now.
          </span>
        </div>
      )}

      {/* 3-slot footer: primary · Add to radar · share (identical placement for
          every viewer — R2). Mobile buttons are taller with radius 11 (8a). */}
      <div className="flex items-center gap-2">
        <div className="flex-[1.3] min-w-0">{primary}</div>
        {onAddToRadar && (
          <button
            type="button"
            onClick={() => onAddToRadar(listing)}
            className={`flex-1 min-w-0 py-3 sm:py-[9px] rounded-[11px] sm:rounded-[10px] border text-[13px] font-semibold text-center ${
              radarActive
                ? "border-[#22A75D] bg-[#ECFDF5] text-[#0E7C3C]"
                : "border-[#D8E0EA] bg-white text-[#33475E] hover:bg-gray-50"
            }`}
          >
            {radarActive ? "On radar" : "Add to radar"}
          </button>
        )}
        {onShare && (
          <button
            type="button"
            onClick={() => onShare(listing)}
            className="flex-none w-[46px] sm:w-[38px] self-stretch inline-flex items-center justify-center rounded-[11px] sm:rounded-[10px] border border-[#D8E0EA] bg-white hover:bg-gray-50"
            aria-label="Share listing"
            title="Share"
          >
            <ShareGlyph />
          </button>
        )}
      </div>
    </div>
  );
};

export default ListingCard;
